// =============================================
// EXCEL EXPORT UTILITIES - FIXED VERSION
// utils/excelExportUtils.js
// =============================================

import * as XLSX from 'xlsx';
import { supabase } from '../lib/supabase';

// ===========================================
// HELPER FUNCTIONS (defined first)
// ===========================================

const groupBy = (array, key) => {
  return array.reduce((result, item) => {
    const groupKey = item[key] || 'unassigned';
    if (!result[groupKey]) result[groupKey] = [];
    result[groupKey].push(item);
    return result;
  }, {});
};

const sum = (array, field) => {
  return array.reduce((total, item) => total + (item[field] || 0), 0);
};

const sumWhere = (array, field, condition) => {
  return array.reduce((total, item) => {
    return total + (condition(item) ? (item[field] || 0) : 0);
  }, 0);
};

const sumRevenue = (array) => {
  return array.reduce((total, item) => {
    // If entry has splits, calculate revenue from splits instead
    if (item.is_split && item.splits && item.splits.length > 0) {
      const splitRevenue = item.splits.reduce((sum, split) => {
        return sum + (split.is_billable ? (split.duration_hours * split.hourly_rate) : 0);
      }, 0);
      return total + splitRevenue;
    }
    // Otherwise use the entry's billable flag
    return total + (item.is_billable ? (item.duration_hours * item.hourly_rate) : 0);
  }, 0);
};


const getProjectStatus = (entries) => {
  const statuses = entries.map(e => e.status);
  const approved = statuses.filter(s => s === 'approved').length;
  const submitted = statuses.filter(s => s === 'submitted').length;
  const rejected = statuses.filter(s => s === 'rejected').length;
  const draft = statuses.filter(s => s === 'draft').length;
  const invoiced = statuses.filter(s => s === 'invoiced').length;
  
  if (statuses.every(s => s === 'approved')) return '[OK] All Approved';
  if (statuses.every(s => s === 'invoiced')) return '[OK] All Invoiced';
  if (rejected > 0) return `[!] ${rejected} Rejected`;
  if (submitted > 0) return `[...] ${submitted} Pending Approval`;
  if (draft > 0 && approved === 0) return `[Draft] ${draft} Draft`;
  return `Mixed: ${approved} approved, ${submitted} pending, ${draft} draft`;
};

const getStatusLabel = (status) => {
  const labels = {
    draft: 'DRAFT',
    submitted: 'SUBMITTED (Pending)',
    approved: 'APPROVED',
    rejected: 'REJECTED',
    invoiced: 'INVOICED'
  };
  return labels[status] || status.toUpperCase();
};


const getStatusSummary = (entries) => {
  const total = entries.length;
  const approved = entries.filter(e => e.status === 'approved').length;
  const submitted = entries.filter(e => e.status === 'submitted').length;
  const draft = entries.filter(e => e.status === 'draft').length;
  
  if (submitted > 0) {
    return `${submitted}/${total} pending approval`;
  }
  if (approved === total) {
    return 'All approved';
  }
  if (draft === total) {
    return 'All draft';
  }
  return `${approved} approved, ${submitted} pending, ${draft} draft`;
};

const getStatusBreakdown = (data) => {
  const total = data.length;
  
  const statuses = ['draft', 'submitted', 'approved', 'rejected', 'invoiced'];
  const breakdown = {};
  
  statuses.forEach(status => {
    const entries = data.filter(e => e.status === status);
    const hours = entries.reduce((sum, e) => sum + (e.duration_hours || 0), 0);
    const revenue = entries.reduce((sum, e) => sum + (e.is_billable ? (e.duration_hours * e.hourly_rate) : 0), 0);
    
    breakdown[status] = {
      count: entries.length,
      hours: hours,
      percentage: total > 0 ? (entries.length / total * 100) : 0,
      revenue: revenue
    };
  });
  
  return breakdown;
};

const getTopClients = (data, limit) => {
  const clientGroups = groupBy(data, 'client_id');
  return Object.entries(clientGroups)
    .map(([id, entries], index) => {
      const name = entries[0]?.client_name || 'No Client';
      const hours = sum(entries, 'duration_hours');
      const billable = sumWhere(entries, 'duration_hours', e => e.is_billable);
      const revenue = sumRevenue(entries);
      const util = hours > 0 ? (billable / hours * 100) : 0;
      return [index + 1, name, hours.toFixed(2), billable.toFixed(2), `$${revenue.toFixed(2)}`, entries.length, `${util.toFixed(1)}%`];
    })
    .sort((a, b) => parseFloat(b[4].replace('$', '')) - parseFloat(a[4].replace('$', '')))
    .slice(0, limit);
};

const getTopProjects = (data, limit) => {
  const projectGroups = groupBy(data, 'project_id');
  return Object.entries(projectGroups)
    .map(([id, entries], index) => {
      const name = entries[0]?.project_name || 'No Project';
      const client = entries[0]?.client_name || 'No Client';
      const hours = sum(entries, 'duration_hours');
      const revenue = sumRevenue(entries);
      const status = getProjectStatus(entries);
      const teamSize = new Set(entries.map(e => e.user_id)).size;
      return [index + 1, name, client, hours.toFixed(2), `$${revenue.toFixed(2)}`, status, teamSize];
    })
    .sort((a, b) => parseFloat(b[3]) - parseFloat(a[3]))
    .slice(0, limit);
};

const getTopUsers = (data, limit) => {
  const userGroups = groupBy(data, 'user_id');
  return Object.entries(userGroups)
    .map(([id, entries], index) => {
      const name = entries[0]?.user_name || 'Unknown User';
      const hours = sum(entries, 'duration_hours');
      const billable = sumWhere(entries, 'duration_hours', e => e.is_billable);
      const revenue = sumRevenue(entries);
      const util = hours > 0 ? (billable / hours * 100) : 0;
      return [index + 1, name, hours.toFixed(2), billable.toFixed(2), `$${revenue.toFixed(2)}`, entries.length, `${util.toFixed(1)}%`];
    })
    .sort((a, b) => parseFloat(b[2]) - parseFloat(a[2]))
    .slice(0, limit);
};

const getWeekNumber = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

// ===========================================
// MAIN EXPORT FUNCTIONS
// ===========================================

/**
 * Fetch time entries with all related data for export
 */
export const fetchTimeEntriesForExport = async (tenantId, filters = {}) => {
  try {
    let query = supabase
      .from('time_entries')
      .select(`
        *,
        user:user_profiles!time_entries_user_id_fkey (
          id,
          full_name,
          email
        ),
        client:clients (
          id,
          name,
          color
        ),
        project:projects (
          id,
          name,
          color,
          hourly_rate
        ),
        approved_by_user:user_profiles!time_entries_approved_by_fkey (
          full_name
        ),
        splits:time_entry_splits (
          id,
          description,
          duration_hours,
          is_billable,
          hourly_rate
        )
      `)
      .eq('tenant_id', tenantId)
      .order('entry_date', { ascending: false });

    // Apply filters
    if (filters.startDate) {
      query = query.gte('entry_date', filters.startDate);
    }
    if (filters.endDate) {
      query = query.lte('entry_date', filters.endDate);
    }
    if (filters.clientId && filters.clientId !== 'all') {
      query = query.eq('client_id', filters.clientId);
    }
    if (filters.projectId && filters.projectId !== 'all') {
      query = query.eq('project_id', filters.projectId);
    }
    if (filters.userId && filters.userId !== 'all') {
      query = query.eq('user_id', filters.userId);
    }
    if (filters.status && filters.status !== 'all') {
      query = query.eq('status', filters.status);
    }

    const { data, error } = await query;

    if (error) throw error;

    // Fetch user billing roles from tenant_memberships
    const userIds = [...new Set(data.map(entry => entry.user_id).filter(Boolean))];
    let userRolesMap = {};
    
    if (userIds.length > 0) {
      const { data: memberships, error: membershipError } = await supabase
        .from('tenant_memberships')
        .select('user_id, bill_rate_role')
        .eq('tenant_id', tenantId)
        .in('user_id', userIds);
      
      if (!membershipError && memberships) {
        userRolesMap = memberships.reduce((acc, m) => {
          acc[m.user_id] = m.bill_rate_role || 'No Role';
          return acc;
        }, {});
      }
    }

    // Transform data for easier processing
    return data.map(entry => ({
      ...entry,
      user_name: entry.user?.full_name || 'Unknown User',
      user_email: entry.user?.email || '',
      user_role: userRolesMap[entry.user_id] || 'No Role',
      client_name: entry.client?.name || 'No Client',
      project_name: entry.project?.name || 'No Project',
      project_color: entry.project?.color || '#3B82F6',
      approved_by_name: entry.approved_by_user?.full_name || '',
      has_splits: entry.is_split && entry.splits?.length > 0
    }));
  } catch (error) {
    console.error('Error fetching data for export:', error);
    throw error;
  }
};

/**
 * Generate comprehensive Excel workbook with drill-down structure
 */
export const generateComprehensiveExcel = async (tenantId, exportConfig, tenantName) => {
  try {
    // Fetch data from Supabase
    const data = await fetchTimeEntriesForExport(tenantId, exportConfig.filters);

    if (!data || data.length === 0) {
      throw new Error('No data available for the selected filters');
    }

    // Create workbook
    const workbook = XLSX.utils.book_new();

    // 1. Summary Sheet
    if (exportConfig.includeSummary) {
      const summarySheet = createSummarySheet(data, tenantName, exportConfig);
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
    }

    // 2. Primary Grouping Sheet
    let primarySheet;
    switch (exportConfig.groupBy) {
      case 'client':
        primarySheet = createClientDrillDownSheet(data, exportConfig.includeDetails);
        XLSX.utils.book_append_sheet(workbook, primarySheet, 'By Client');
        break;
      case 'project':
        primarySheet = createProjectDrillDownSheet(data, exportConfig.includeDetails);
        XLSX.utils.book_append_sheet(workbook, primarySheet, 'By Project');
        break;
      case 'user':
        primarySheet = createUserDrillDownSheet(data, exportConfig.includeDetails);
        XLSX.utils.book_append_sheet(workbook, primarySheet, 'By User');
        break;
      default:
        primarySheet = createClientDrillDownSheet(data, exportConfig.includeDetails);
        XLSX.utils.book_append_sheet(workbook, primarySheet, 'By Client');
    }

    // 3. Additional sheets for comprehensive view
    if (exportConfig.includeAllViews) {
      if (exportConfig.groupBy !== 'client') {
        const clientSheet = createClientDrillDownSheet(data, false);
        XLSX.utils.book_append_sheet(workbook, clientSheet, 'By Client');
      }
      if (exportConfig.groupBy !== 'project') {
        const projectSheet = createProjectDrillDownSheet(data, false);
        XLSX.utils.book_append_sheet(workbook, projectSheet, 'By Project');
      }
      if (exportConfig.groupBy !== 'user') {
        const userSheet = createUserDrillDownSheet(data, false);
        XLSX.utils.book_append_sheet(workbook, userSheet, 'By User');
      }
      // Always include Role sheet when all views are selected
      const roleSheet = createRoleDrillDownSheet(data, false);
      XLSX.utils.book_append_sheet(workbook, roleSheet, 'By Role');
    }

    // 4. Detailed Entries Sheet
    const detailedSheet = createDetailedEntriesSheet(data);
    XLSX.utils.book_append_sheet(workbook, detailedSheet, 'All Entries');

    // 5. Pivot Data Sheet (for Excel pivot tables)
    const pivotSheet = createPivotDataSheet(data);
    XLSX.utils.book_append_sheet(workbook, pivotSheet, 'Pivot Data');

    // Generate filename
    const dateStr = new Date().toISOString().split('T')[0];
    const filename = `TimeTracking_${exportConfig.groupBy}_${dateStr}.xlsx`;

    // Write and download
    XLSX.writeFile(workbook, filename);

    return { success: true, filename };
  } catch (error) {
    console.error('Error generating Excel:', error);
    throw error;
  }
};

/**
 * Create Summary Sheet
 */
const createSummarySheet = (data, tenantName, config) => {
  const totalHours = data.reduce((sum, e) => sum + (e.duration_hours || 0), 0);
  const billableHours = data.reduce((sum, e) => sum + (e.is_billable ? e.duration_hours : 0), 0);
  const nonBillableHours = totalHours - billableHours;
  const totalRevenue = data.reduce((sum, e) => sum + (e.is_billable ? (e.duration_hours * e.hourly_rate) : 0), 0);
  const avgRate = billableHours > 0 ? totalRevenue / billableHours : 0;
  const utilization = totalHours > 0 ? (billableHours / totalHours * 100) : 0;

  const uniqueClients = new Set(data.map(e => e.client_id).filter(Boolean)).size;
  const uniqueProjects = new Set(data.map(e => e.project_id).filter(Boolean)).size;
  const uniqueUsers = new Set(data.map(e => e.user_id).filter(Boolean)).size;

  // Status breakdown
  const statusBreakdown = getStatusBreakdown(data);

  const rows = [
    ['TIME TRACKING REPORT - EXECUTIVE SUMMARY'],
    [''],
    ['Report Generated:', new Date().toLocaleString()],
    ['Organization:', tenantName],
    ['Report Period:', `${config.filters?.startDate || 'All Time'} to ${config.filters?.endDate || 'Present'}`],
    [''],
    ['OVERVIEW METRICS'],
    ['Metric', 'Value'],
    ['Total Time Entries', data.length],
    ['Total Hours Logged', totalHours.toFixed(2)],
    ['Billable Hours', billableHours.toFixed(2)],
    ['Non-Billable Hours', nonBillableHours.toFixed(2)],
    ['Total Revenue', `$${totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
    ['Average Hourly Rate', `$${avgRate.toFixed(2)}`],
    ['Utilization Rate', `${utilization.toFixed(1)}%`],
    [''],
    ['Active Clients', uniqueClients],
    ['Active Projects', uniqueProjects],
    ['Team Members', uniqueUsers],
    [''],
    ['STATUS BREAKDOWN - WORKFLOW OVERVIEW'],
    ['Status', 'Entries', 'Hours', '% of Total', 'Revenue'],
    ['Draft (Not Submitted)', statusBreakdown.draft.count, statusBreakdown.draft.hours.toFixed(2), `${statusBreakdown.draft.percentage.toFixed(1)}%`, `$${statusBreakdown.draft.revenue.toFixed(2)}`],
    ['Submitted (Pending Approval)', statusBreakdown.submitted.count, statusBreakdown.submitted.hours.toFixed(2), `${statusBreakdown.submitted.percentage.toFixed(1)}%`, `$${statusBreakdown.submitted.revenue.toFixed(2)}`],
    ['Approved (Validated)', statusBreakdown.approved.count, statusBreakdown.approved.hours.toFixed(2), `${statusBreakdown.approved.percentage.toFixed(1)}%`, `$${statusBreakdown.approved.revenue.toFixed(2)}`],
    ['Rejected', statusBreakdown.rejected.count, statusBreakdown.rejected.hours.toFixed(2), `${statusBreakdown.rejected.percentage.toFixed(1)}%`, `$${statusBreakdown.rejected.revenue.toFixed(2)}`],
    ['Invoiced', statusBreakdown.invoiced.count, statusBreakdown.invoiced.hours.toFixed(2), `${statusBreakdown.invoiced.percentage.toFixed(1)}%`, `$${statusBreakdown.invoiced.revenue.toFixed(2)}`],
    [''],
    ['âš ï¸ ACTION REQUIRED'],
    ['Entries Pending Approval:', statusBreakdown.submitted.count],
    ['Hours Awaiting Validation:', statusBreakdown.submitted.hours.toFixed(2)],
    [''],
    ['TOP 10 CLIENTS BY REVENUE'],
    ['Rank', 'Client', 'Hours', 'Billable Hours', 'Revenue', 'Entries', 'Utilization %'],
    ...getTopClients(data, 10),
    [''],
    ['TOP 10 PROJECTS BY HOURS'],
    ['Rank', 'Project', 'Client', 'Hours', 'Revenue', 'Status', 'Team Size'],
    ...getTopProjects(data, 10),
    [''],
    ['TOP 10 TEAM MEMBERS BY HOURS'],
    ['Rank', 'Team Member', 'Hours', 'Billable Hours', 'Revenue', 'Entries', 'Utilization %'],
    ...getTopUsers(data, 10)
  ];

  const sheet = XLSX.utils.aoa_to_sheet(rows);
  
  // Set column widths
  sheet['!cols'] = [
    { wch: 30 },
    { wch: 25 },
    { wch: 15 },
    { wch: 15 },
    { wch: 18 },
    { wch: 12 },
    { wch: 15 }
  ];

  return sheet;
};

/**
 * Create Client Drill-Down Sheet
 */
const createClientDrillDownSheet = (data, includeDetails) => {
  const rows = [
    ['BY CLIENT - DETAILED BREAKDOWN'],
    [''],
    ['Hierarchical view: Client > Project > Individual Entries'],
    ['Note: Check STATUS column to see validation state (Draft/Submitted/Approved/Rejected/Invoiced)'],
    ['']
  ];

  const clientGroups = groupBy(data, 'client_id');

  Object.entries(clientGroups).forEach(([clientId, clientEntries]) => {
    const clientName = clientEntries[0]?.client_name || 'No Client';
    const totalHours = sum(clientEntries, 'duration_hours');
    const billableHours = sumWhere(clientEntries, 'duration_hours', e => e.is_billable);
    const revenue = sumRevenue(clientEntries);
    const utilization = totalHours > 0 ? (billableHours / totalHours * 100) : 0;
    
    // Status summary for this client
    const statusSummary = getStatusSummary(clientEntries);

    // Client Header Row
    rows.push([
      `CLIENT: ${clientName}`,
      `${clientEntries.length} entries`,
      `${totalHours.toFixed(2)}h total`,
      `${billableHours.toFixed(2)}h billable`,
      `$${revenue.toLocaleString()}`,
      `${utilization.toFixed(1)}% utilization`,
      `Status: ${statusSummary}`
    ]);
    rows.push(['']);

    // Column headers for this client
    rows.push(['  Project', 'Hours', 'Billable', 'Revenue', 'Entries', 'Validation Status']);

    // Group by project within this client
    const projectGroups = groupBy(clientEntries, 'project_id');
    
    Object.entries(projectGroups).forEach(([projectId, projectEntries]) => {
      const projectName = projectEntries[0]?.project_name || 'No Project';
      const projHours = sum(projectEntries, 'duration_hours');
      const projBillable = sumWhere(projectEntries, 'duration_hours', e => e.is_billable);
      const projRevenue = sumRevenue(projectEntries);
      const projStatus = getProjectStatus(projectEntries);
      
      rows.push([
        `    ${projectName}`,
        projHours.toFixed(2),
        projBillable.toFixed(2),
        `$${projRevenue.toFixed(2)}`,
        projectEntries.length,
        projStatus
      ]);

      // Include individual entries if requested
      if (includeDetails) {
        rows.push(['      Date', 'User', 'Task', 'Hours', 'Rate', 'Amount', 'Billable', 'STATUS']);
        
        projectEntries.forEach(entry => {
          const amount = entry.is_billable ? (entry.duration_hours * entry.hourly_rate) : 0;
          rows.push([
            `        ${entry.entry_date}`,
            entry.user_name,
            entry.task_name,
            entry.duration_hours.toFixed(2),
            `$${entry.hourly_rate.toFixed(2)}`,
            `$${amount.toFixed(2)}`,
            entry.is_billable ? 'Yes' : 'No',
            getStatusLabel(entry.status)
          ]);
        });
        rows.push(['']);
      }
    });

    rows.push(['']);
    rows.push(['']); // Extra space between clients
  });

  return XLSX.utils.aoa_to_sheet(rows);
};

/**
 * Create Project Drill-Down Sheet
 */
const createProjectDrillDownSheet = (data, includeDetails) => {
  const rows = [
    ['BY PROJECT - DETAILED BREAKDOWN'],
    [''],
    ['Hierarchical view: Project > User > Individual Entries'],
    ['Note: Check STATUS column to see validation state (Draft/Submitted/Approved/Rejected/Invoiced)'],
    ['']
  ];

  const projectGroups = groupBy(data, 'project_id');

  Object.entries(projectGroups).forEach(([projectId, projectEntries]) => {
    const projectName = projectEntries[0]?.project_name || 'No Project';
    const clientName = projectEntries[0]?.client_name || 'No Client';
    const totalHours = sum(projectEntries, 'duration_hours');
    const billableHours = sumWhere(projectEntries, 'duration_hours', e => e.is_billable);
    const revenue = sumRevenue(projectEntries);
    const statusSummary = getStatusSummary(projectEntries);

    // Project Header Row
    rows.push([
      `PROJECT: ${projectName}`,
      `Client: ${clientName}`,
      `${totalHours.toFixed(2)}h`,
      `$${revenue.toLocaleString()}`,
      `${projectEntries.length} entries`,
      `Status: ${statusSummary}`
    ]);
    rows.push(['']);
    rows.push(['  User', 'Hours', 'Billable', 'Revenue', 'Entries', 'Avg Rate', 'Validation Status']);

    // Group by user within this project
    const userGroups = groupBy(projectEntries, 'user_id');
    
    Object.entries(userGroups).forEach(([userId, userEntries]) => {
      const userName = userEntries[0]?.user_name || 'Unknown User';
      const userHours = sum(userEntries, 'duration_hours');
      const userBillable = sumWhere(userEntries, 'duration_hours', e => e.is_billable);
      const userRevenue = sumRevenue(userEntries);
      const avgRate = userBillable > 0 ? userRevenue / userBillable : 0;
      const userStatus = getProjectStatus(userEntries);
      
      rows.push([
        `    ${userName}`,
        userHours.toFixed(2),
        userBillable.toFixed(2),
        `$${userRevenue.toFixed(2)}`,
        userEntries.length,
        `$${avgRate.toFixed(2)}`,
        userStatus
      ]);

      // Include individual entries if requested
      if (includeDetails) {
        rows.push(['      Date', 'Task', 'Hours', 'Rate', 'Billable', 'STATUS']);
        
        userEntries.forEach(entry => {
          rows.push([
            `        ${entry.entry_date}`,
            entry.task_name,
            entry.duration_hours.toFixed(2),
            `$${entry.hourly_rate.toFixed(2)}`,
            entry.is_billable ? 'Yes' : 'No',
            getStatusLabel(entry.status)
          ]);
        });
        rows.push(['']);
      }
    });

    rows.push(['']);
    rows.push(['']);
  });

  return XLSX.utils.aoa_to_sheet(rows);
};

/**
 * Create User Drill-Down Sheet
 */
const createUserDrillDownSheet = (data, includeDetails) => {
  const rows = [
    ['BY USER - DETAILED BREAKDOWN'],
    [''],
    ['Hierarchical view: User > Client > Project > Individual Entries'],
    ['Note: Check STATUS column to see validation state (Draft/Submitted/Approved/Rejected/Invoiced)'],
    ['']
  ];

  const userGroups = groupBy(data, 'user_id');

  Object.entries(userGroups).forEach(([userId, userEntries]) => {
    const userName = userEntries[0]?.user_name || 'Unknown User';
    const totalHours = sum(userEntries, 'duration_hours');
    const billableHours = sumWhere(userEntries, 'duration_hours', e => e.is_billable);
    const revenue = sumRevenue(userEntries);
    const utilization = totalHours > 0 ? (billableHours / totalHours * 100) : 0;
    const statusSummary = getStatusSummary(userEntries);

    // User Header Row
    rows.push([
      `USER: ${userName}`,
      `${totalHours.toFixed(2)}h total`,
      `${billableHours.toFixed(2)}h billable`,
      `$${revenue.toLocaleString()}`,
      `${utilization.toFixed(1)}% utilization`,
      `Status: ${statusSummary}`
    ]);
    rows.push(['']);
    rows.push(['  Client', 'Project', 'Hours', 'Revenue', 'Entries', 'Validation Status']);

    // Group by client within this user
    const clientGroups = groupBy(userEntries, 'client_id');
    
    Object.entries(clientGroups).forEach(([clientId, clientEntries]) => {
      const clientName = clientEntries[0]?.client_name || 'No Client';
      
      // Group by project within client
      const projectGroups = groupBy(clientEntries, 'project_id');
      
      Object.entries(projectGroups).forEach(([projectId, projectEntries], index) => {
        const projectName = projectEntries[0]?.project_name || 'No Project';
        const projHours = sum(projectEntries, 'duration_hours');
        const projRevenue = sumRevenue(projectEntries);
        const projStatus = getProjectStatus(projectEntries);
        
        rows.push([
          index === 0 ? `    ${clientName}` : '',
          `      ${projectName}`,
          projHours.toFixed(2),
          `$${projRevenue.toFixed(2)}`,
          projectEntries.length,
          projStatus
        ]);

        // Include individual entries if requested
        if (includeDetails) {
          projectEntries.forEach(entry => {
            const amount = entry.is_billable ? (entry.duration_hours * entry.hourly_rate) : 0;
            rows.push([
              '',
              `        ${entry.entry_date} - ${entry.task_name}`,
              entry.duration_hours.toFixed(2),
              `$${amount.toFixed(2)}`,
              getStatusLabel(entry.status),
              entry.is_billable ? 'Billable' : 'Non-billable'
            ]);
          });
        }
      });
    });

    rows.push(['']);
    rows.push(['']);
  });

  return XLSX.utils.aoa_to_sheet(rows);
};

/**
 * Create Role Drill-Down Sheet
 */
const createRoleDrillDownSheet = (data, includeDetails) => {
  const rows = [
    ['BY ROLE - DETAILED BREAKDOWN'],
    [''],
    ['Hierarchical view: Role > User > Client > Project > Individual Entries'],
    ['Note: Check STATUS column to see validation state (Draft/Submitted/Approved/Rejected/Invoiced)'],
    ['']
  ];

  const roleGroups = groupBy(data, 'user_role');

  Object.entries(roleGroups).forEach(([roleName, roleEntries]) => {
    const displayRoleName = roleName || 'No Role';
    const totalHours = sum(roleEntries, 'duration_hours');
    const billableHours = sumWhere(roleEntries, 'duration_hours', e => e.is_billable);
    const revenue = sumRevenue(roleEntries);
    const utilization = totalHours > 0 ? (billableHours / totalHours * 100) : 0;
    const statusSummary = getStatusSummary(roleEntries);
    const uniqueUsers = new Set(roleEntries.map(e => e.user_id)).size;

    // Role Header Row
    rows.push([
      `ROLE: ${displayRoleName}`,
      `${uniqueUsers} team member${uniqueUsers !== 1 ? 's' : ''}`,
      `${totalHours.toFixed(2)}h total`,
      `${billableHours.toFixed(2)}h billable`,
      `$${revenue.toLocaleString()}`,
      `${utilization.toFixed(1)}% utilization`
    ]);
    rows.push(['']);
    rows.push(['  User', 'Client', 'Project', 'Hours', 'Revenue', 'Validation Status']);

    // Group by user within this role
    const userGroups = groupBy(roleEntries, 'user_id');
    
    Object.entries(userGroups).forEach(([userId, userEntries]) => {
      const userName = userEntries[0]?.user_name || 'Unknown User';
      const userHours = sum(userEntries, 'duration_hours');
      const userRevenue = sumRevenue(userEntries);
      
      // Group by client within user
      const clientGroups = groupBy(userEntries, 'client_id');
      let isFirstUserRow = true;
      
      Object.entries(clientGroups).forEach(([clientId, clientEntries]) => {
        const clientName = clientEntries[0]?.client_name || 'No Client';
        
        // Group by project within client
        const projectGroups = groupBy(clientEntries, 'project_id');
        let isFirstClientRow = true;
        
        Object.entries(projectGroups).forEach(([projectId, projectEntries]) => {
          const projectName = projectEntries[0]?.project_name || 'No Project';
          const projHours = sum(projectEntries, 'duration_hours');
          const projRevenue = sumRevenue(projectEntries);
          const projStatus = getProjectStatus(projectEntries);
          
          rows.push([
            isFirstUserRow ? `    ${userName}` : '',
            isFirstClientRow ? `      ${clientName}` : '',
            `        ${projectName}`,
            projHours.toFixed(2),
            `$${projRevenue.toFixed(2)}`,
            projStatus
          ]);
          
          isFirstUserRow = false;
          isFirstClientRow = false;

          // Include individual entries if requested
          if (includeDetails) {
            projectEntries.forEach(entry => {
              const amount = entry.is_billable ? (entry.duration_hours * entry.hourly_rate) : 0;
              rows.push([
                '',
                '',
                `          ${entry.entry_date} - ${entry.task_name}`,
                entry.duration_hours.toFixed(2),
                `$${amount.toFixed(2)}`,
                getStatusLabel(entry.status)
              ]);
            });
          }
        });
      });
    });

    rows.push(['']);
    rows.push(['']);
  });

  return XLSX.utils.aoa_to_sheet(rows);
};

/**
 * Create Detailed Entries Sheet
 */
const createDetailedEntriesSheet = (data) => {
  const rows = [
    ['ALL TIME ENTRIES - COMPLETE DETAIL VIEW'],
    [''],
    ['Note: STATUS column shows validation state - Use this to track workflow'],
    ['Draft = Not submitted | Submitted = Pending approval | Approved = Validated | Rejected = Needs revision | Invoiced = Billed'],
    [''],
    ['Date', 'User', 'Role', 'Client', 'Project', 'Task', 'Description', 'Hours', 'Rate', 'Amount', 'Billable', 'STATUS', 'Approved By', 'Entry Date']
  ];

  data.forEach(entry => {
    const amount = entry.is_billable ? (entry.duration_hours * entry.hourly_rate) : 0;
    rows.push([
      entry.entry_date,
      entry.user_name,
      entry.user_role || 'No Role',
      entry.client_name,
      entry.project_name,
      entry.task_name,
      entry.description || '',
      entry.duration_hours.toFixed(2),
      `$${entry.hourly_rate.toFixed(2)}`,
      `$${amount.toFixed(2)}`,
      entry.is_billable ? 'Yes' : 'No',
      getStatusLabel(entry.status),
      entry.approved_by_name || '',
      new Date(entry.created_at).toLocaleDateString()
    ]);
  });

  const sheet = XLSX.utils.aoa_to_sheet(rows);
  sheet['!cols'] = [
    { wch: 12 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 },
    { wch: 25 }, { wch: 30 }, { wch: 10 }, { wch: 10 },
    { wch: 12 }, { wch: 10 }, { wch: 20 }, { wch: 20 }, { wch: 12 }
  ];

  return sheet;
};

/**
 * Create Pivot Data Sheet (optimized for Excel pivot tables)
 */
const createPivotDataSheet = (data) => {
  const rows = [
    ['Date', 'Year', 'Month', 'Week', 'DayOfWeek', 'User', 'Role', 'Client', 'Project', 'Task', 'Hours', 'Rate', 'Revenue', 'Billable', 'STATUS', 'Submitted', 'Approved', 'Rejected']
  ];

  data.forEach(entry => {
    const date = new Date(entry.entry_date);
    const revenue = entry.is_billable ? (entry.duration_hours * entry.hourly_rate) : 0;
    
    rows.push([
      entry.entry_date,
      date.getFullYear(),
      date.getMonth() + 1,
      getWeekNumber(date),
      date.toLocaleDateString('en-US', { weekday: 'long' }),
      entry.user_name,
      entry.user_role || 'No Role',
      entry.client_name,
      entry.project_name,
      entry.task_name,
      entry.duration_hours,
      entry.hourly_rate,
      revenue,
      entry.is_billable ? 'Yes' : 'No',
      getStatusLabel(entry.status),
      entry.status === 'submitted' ? 'Yes' : 'No',
      entry.status === 'approved' ? 'Yes' : 'No',
      entry.status === 'rejected' ? 'Yes' : 'No'
    ]);
  });

  return XLSX.utils.aoa_to_sheet(rows);
};